import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "wouter";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/ui/page-hero";
import { BeforeAfterSlider } from "@/components/ui/before-after-slider";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ui/scroll-reveal";
import { PictureImage } from "@/components/ui/picture-image";
import { cn } from "@/lib/utils";
import { heroImages, heroMobileImages } from "@/lib/image-map";
import { pageSchema } from "@/lib/organization";
import { gallerySliders, galleryCategories, photosForCategory } from "@/lib/gallery-data";

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const filteredPhotos = photosForCategory(activeCategory);

  const filteredSliders =
    activeCategory === "All"
      ? gallerySliders
      : gallerySliders.filter((slider) => slider.category === activeCategory);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const showPrev = useCallback(() => {
    setLightboxIndex((current) =>
      current === null ? null : (current - 1 + filteredPhotos.length) % filteredPhotos.length
    );
  }, [filteredPhotos.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((current) =>
      current === null ? null : (current + 1) % filteredPhotos.length
    );
  }, [filteredPhotos.length]);

  const isLightboxOpen = lightboxIndex !== null;

  useEffect(() => {
    if (!isLightboxOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
      if (e.key === "Tab") {
        const dialog = lightboxRef.current;
        if (!dialog) return;
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isLightboxOpen, closeLightbox, showPrev, showNext]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setLightboxIndex(null);
  };

  const lightboxPhoto = lightboxIndex !== null ? filteredPhotos[lightboxIndex] : null;

  return (
    <>
      <SEO
        title="Project Gallery | TLC Associates LLC"
        description="View landscape maintenance and renovation projects across Norfolk County completed by TLC Associates LLC for local properties."
        path="/gallery"
        schema={pageSchema("/gallery", "CollectionPage", "Project Gallery | TLC Associates LLC", "View landscape maintenance and renovation projects across Norfolk County completed by TLC Associates LLC for local properties.")}
      />

      <PageHero
        imageSrc={heroImages["/"]}
        mobileImageSrc={heroMobileImages["/"]}
        eyebrow="Landscaping Projects Across Norfolk County"
        headline="See Our Completed Landscape Projects"
        description="TLC Associates LLC project photos include maintenance, planting, renovation, tree care, and commercial work."
      />

      <section className="py-16 md:py-20 bg-[#f7f3ed]">
        <div className="container mx-auto px-4 max-w-6xl">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl text-[#1a3a1a] mb-4 text-center">
              Our Work
              <div className="h-1 w-24 bg-[#f5c828] mx-auto mt-4 rounded-full"></div>
            </h2>
            <p className="text-center text-[#1a1a1a]/70 max-w-2xl mx-auto mb-10">
              Filter by service to see the projects that matter most to you.
            </p>
          </ScrollReveal>

          <div className="flex flex-wrap justify-center gap-3 mb-12" role="group" aria-label="Filter gallery by category">
            {galleryCategories.map((category) => (
              <button
                key={category.value}
                onClick={() => handleCategoryChange(category.value)}
                aria-pressed={activeCategory === category.value}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-colors border-2",
                  activeCategory === category.value
                    ? "bg-[#1a3a1a] border-[#1a3a1a] text-white"
                    : "bg-white border-[#1a3a1a]/20 text-[#1a3a1a] hover:border-[#2a7a2a] hover:text-[#2a7a2a]"
                )}
              >
                {category.label}
              </button>
            ))}
          </div>

          {filteredPhotos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {filteredPhotos.map((photo, index) => (
                <button
                  key={photo.src}
                  onClick={() => setLightboxIndex(index)}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7a2a] focus-visible:ring-offset-2"
                  aria-label={`View larger: ${photo.alt}`}
                >
                  <PictureImage
                    src={photo.src}
                    smallSrc={photo.smallSrc}
                    alt={photo.alt}
                    width={800}
                    height={600}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p className="text-white text-xs md:text-sm font-medium p-3 text-left">
                      {photo.alt}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#1a1a1a]/70 py-12">
              No photos in this category yet. Check back soon.
            </p>
          )}
        </div>
      </section>

      {filteredSliders.length > 0 && (
        <section className="py-24 bg-[#1a3a1a]">
          <div className="container mx-auto px-4 max-w-6xl">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl text-white mb-4 text-center">
                Before & After
                <div className="h-1 w-24 bg-[#f5c828] mx-auto mt-4 rounded-full"></div>
              </h2>
              <p className="text-center text-white/70 max-w-2xl mx-auto mb-16">
                Drag the slider on each photo to see the transformation.
              </p>
            </ScrollReveal>

            <StaggerContainer
              key={activeCategory}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12"
              staggerDelay={0.08}
            >
              {filteredSliders.map(({ id, set, caption }) => (
                <StaggerItem key={id}>
                  <BeforeAfterSlider
                    beforeImage={set.before.large}
                    afterImage={set.after.large}
                    beforeImageSmall={set.before.small}
                    afterImageSmall={set.after.small}
                    altText={set.alt}
                    caption={caption}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      <section className="py-24 bg-[#c84060]">
        <ScrollReveal>
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl text-white mb-6">Ready for Your Own Transformation?</h2>
            <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
              Contact us today for a free walkthrough of your property and a detailed estimate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact#estimate-form"
                className="bg-white text-[#1a3a1a] px-8 py-4 rounded-lg font-medium text-lg hover:bg-gray-100 transition-colors"
              >
                Request Your Free Estimate
              </Link>
              <a
                href="tel:5085051539"
                className="text-white font-medium text-lg px-6 py-3 hover:underline"
              >
                Or call 508-505-1539
              </a>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {lightboxPhoto && (
        <div
          ref={lightboxRef}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={lightboxPhoto.alt}
          onClick={closeLightbox}
        >
          <button
            ref={closeButtonRef}
            onClick={closeLightbox}
            aria-label="Close image viewer"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
            style={{ top: `calc(env(safe-area-inset-top, 0px) + 1rem)` }}
          >
            <X className="w-8 h-8" aria-hidden="true" />
          </button>

          {filteredPhotos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                aria-label="Previous photo"
                className="absolute left-2 md:left-6 text-white/80 hover:text-white p-2 z-10 bg-black/40 rounded-full"
              >
                <ChevronLeft className="w-7 h-7" aria-hidden="true" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                aria-label="Next photo"
                className="absolute right-2 md:right-6 text-white/80 hover:text-white p-2 z-10 bg-black/40 rounded-full"
              >
                <ChevronRight className="w-7 h-7" aria-hidden="true" />
              </button>
            </>
          )}

          <figure
            className="max-w-5xl max-h-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <PictureImage
              src={lightboxPhoto.fullSrc ?? lightboxPhoto.src}
              alt={lightboxPhoto.alt}
              width={1200}
              height={800}
              disableSmallVariant
              className="max-h-[80vh] w-auto max-w-full object-contain rounded-lg"
            />
            <figcaption className="text-white/90 text-sm md:text-base text-center">
              {lightboxPhoto.alt}
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
}
